import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import TableSkeleton from '../../../components/TableSkeleton';
import ExportButton from '../components/ExportButton';
import styles from '../OrderRulesReport.styles';

/** Recommendation / confidence truth table — mirrors the design reference matrix exactly. */
const CONFIDENCE_LOGIC_ROWS: Array<[string, string, string, string, string]> = [
  ['N', 'N', 'N', 'N', 'High'],
  ['N', 'N', 'Y', 'Y', 'Low'],
  ['N', 'Y', 'N', 'N', 'Low'],
  ['N', 'Y', 'Y', 'N', 'Medium'],
  ['Y', 'N', 'N', 'Y', 'Medium'],
  ['Y', 'N', 'Y', 'Y', 'Low'],
  ['Y', 'Y', 'N', 'N', 'Low'],
  ['Y', 'Y', 'Y', 'Y', 'High'],
];

const CONFIDENCE_COLORS: Record<string, string> = {
  High: '#8FDCA4',
  Medium: '#F4C95D',
  Low: '#9BCBF5',
};

const CONFIDENCE_HEADERS = ['PY+6 \u2265 Layer', 'PY-6 \u2265 Layer', 'CY-6 \u2265 Layer', 'Turn On?', 'Confidence'];

/** Renders the full 8-row recommendation/confidence logic matrix used in the Confidence column tooltip. */
const ConfidenceLogicTooltipContent: React.FC = () => (
  <View>
    <Text style={confidenceMatrixStyles.description}>
      Each weekly average is compared to cases per layer. Y means the average meets or exceeds the layer quantity.
    </Text>
    <View style={confidenceMatrixStyles.table}>
      <View style={confidenceMatrixStyles.row}>
        {CONFIDENCE_HEADERS.map((header) => (
          <View key={header} style={[confidenceMatrixStyles.cell, confidenceMatrixStyles.headerCell]}>
            <Text style={confidenceMatrixStyles.headerText}>{header}</Text>
          </View>
        ))}
      </View>
      {CONFIDENCE_LOGIC_ROWS.map((row, rowIndex) => (
        <View key={rowIndex} style={confidenceMatrixStyles.row}>
          {row.map((value, colIndex) => {
            const isConfidenceCol = colIndex === row.length - 1;
            const isTurnOnCol = colIndex === row.length - 2;
            return (
              <View key={colIndex} style={confidenceMatrixStyles.cell}>
                <Text
                  style={[
                    confidenceMatrixStyles.cellText,
                    (isConfidenceCol || isTurnOnCol) && confidenceMatrixStyles.cellTextStrong,
                    isConfidenceCol && { color: CONFIDENCE_COLORS[value] },
                  ]}
                >
                  {value}
                </Text>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  </View>
);

const confidenceMatrixStyles = StyleSheet.create({
  description: {
    fontSize: 12,
    lineHeight: 17,
    color: '#E5E5E5',
    marginBottom: 8,
  },
  table: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
  },
  row: {
    flexDirection: 'row',
  },
  cell: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
    paddingVertical: 3,
    paddingHorizontal: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCell: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  headerText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
    color: '#E5E5E5',
    textAlign: 'center',
  },
  cellText: {
    fontSize: 11,
    lineHeight: 14,
    color: '#E5E5E5',
    textAlign: 'center',
  },
  cellTextStrong: {
    fontWeight: '700',
  },
});

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
}

interface FlexibleColumn {
  key: string;
  label: string;
  flex: number;
  align?: 'left' | 'right' | 'center';
}

interface RecommendationsTabProps {
  loading: boolean;
  recommendationRows: Array<Record<string, any>>;
  openRecommendationRows: Array<Record<string, any>>;
  recommendationWindowStart?: string;
  recommendationWindowEnd?: string;
  /** Server-side sort state for the paginated SKU Recommendations table. */
  sort: { col: string | null; dir: 'asc' | 'desc' | null };
  /** Fires the sortBy/sortOrder API call for the SKU Recommendations table. */
  onSort: (colKey: string) => void;
  /** Pagination bar for the SKU Recommendations table, rendered directly under it. */
  pagination?: React.ReactNode;
  /** Triggers the export API for this tab. */
  onExport: () => Promise<void>;
}

type SortDir = 'asc' | 'desc' | null;

interface SortState {
  col: string | null;
  dir: SortDir;
}

/** Numeric column keys for the SKU Recommendations table */
const RECO_NUMERIC_KEYS = new Set([
  'casesPerLayer', 'pyPlus6AvgCases',
  'pyMinus6AvgCases', 'cyMinus6AvgCases', 'customerBp',
]);

/** Numeric column keys for the Open Recommendations table */
const OPEN_RECO_NUMERIC_KEYS = new Set(['high', 'medium', 'low', 'total']);

const compareValues = (a: any, b: any, numericKeys: Set<string>, key: string): number => {
  if (numericKeys.has(key)) return Number(a ?? 0) - Number(b ?? 0);
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const applySort = (
  rows: Array<Record<string, any>>,
  sort: SortState,
  numericKeys: Set<string>,
): Array<Record<string, any>> => {
  if (!sort.col || !sort.dir) return rows;
  const col = sort.col;
  const dir = sort.dir;
  return [...rows].sort((a, b) => {
    const cmp = compareValues(a[col], b[col], numericKeys, col);
    return dir === 'asc' ? cmp : -cmp;
  });
};

const makeHandleSort = (setSort: React.Dispatch<React.SetStateAction<SortState>>) =>
  (colKey: string) => {
    setSort((current) => {
      if (current.col !== colKey) return { col: colKey, dir: 'asc' };
      if (current.dir === 'asc') return { col: colKey, dir: 'desc' };
      if (current.dir === 'desc') return { col: null, dir: null };
      return { col: colKey, dir: 'asc' };
    });
  };

/**
 * Formats a date string (e.g. "2026-06-07") into a human-readable label
 * like "Jun 7" or "Jun 13, 2026" (year only appended to the end date).
 */
const formatWindowDate = (dateStr: string, includeYear: boolean): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  return includeYear ? `${month} ${day}, ${date.getFullYear()}` : `${month} ${day}`;
};

const renderConfidencePill = (value: string) => {
  const normalized = value.trim().toLowerCase();
  const palette =
    normalized === 'high'
      ? { bg: '#CAF3C6', text: '#19759A' }
      : normalized === 'medium'
      ? { bg: '#FFF3D8', text: '#8A6500' }
      : { bg: '#FFE7E1', text: '#C43A17' };

  return (
    <View style={[styles.badge, { backgroundColor: palette.bg }]}> 
      <Text style={[styles.badgeText, { color: palette.text }]}>{value}</Text>
    </View>
  );
};

const recommendationColumns: TableColumn[] = [
  { key: 'location', label: 'Location', minWidth: 150, align: 'left' },
  { key: 'customer', label: 'Customer Name', minWidth: 190, align: 'left' },
  { key: 'materialNumber', label: 'Material No.', minWidth: 130, align: 'right' },
  { key: 'packageName', label: 'Package', minWidth: 150, align: 'left' },
  { key: 'description', label: 'Material Description', minWidth: 300, align: 'left' },
  { key: 'skuEligible', label: 'SKU Eligible', minWidth: 150, align: 'center' },
  { key: 'recommendation', label: 'Recommend', minWidth: 140, align: 'center' },
  { key: 'confidence', label: 'Confidence', minWidth: 140, align: 'center' },
  { key: 'customerEnrolled', label: 'Customer Enrolled', minWidth: 190, align: 'center' },
  // Delivery Days / Wk column is not needed for now.
  // { key: 'deliveryDaysPerWeek', label: 'Delivery Days / Wk', minWidth: 140, align: 'right' },
  { key: 'casesPerLayer', label: 'Cases / Layer', minWidth: 150, align: 'right' },
  { key: 'pyPlus6AvgCases', label: 'PY+6 Avg Cases/Week', minWidth: 160, align: 'right' },
  { key: 'pyMinus6AvgCases', label: 'PY-6 Avg Cases/Week', minWidth: 160, align: 'right' },
  { key: 'cyMinus6AvgCases', label: 'CY-6 Avg Cases/Week', minWidth: 160, align: 'right' },
  { key: 'pyPlus6GteLayer', label: 'PY+6 Avg >= Layer', minWidth: 150, align: 'center' },
  { key: 'pyMinus6GteLayer', label: 'PY-6 Avg >= Layer', minWidth: 150, align: 'center' },
  { key: 'cyMinus6GteLayer', label: 'CY-6 Avg >= Layer', minWidth: 150, align: 'center' },
  { key: 'nextAction', label: 'Next Enrollment Start Date', minWidth: 150, align: 'left' },
  { key: 'region', label: 'Region', minWidth: 120, align: 'left' },
  { key: 'market', label: 'Market', minWidth: 170, align: 'left' },
  { key: 'customerBp', label: 'Customer BP', minWidth: 130, align: 'right' },
];

const recommendationHeaderTooltips: Record<string, string> = {
  skuEligible: 'SKU is eligible for order rules at this sales location and appears in the Order Rule Product table.',
  recommendation: 'Recommendation to Turn On (Y) or Turn Off (N), derived from the three average vs layer comparisons. See confidence info icon for the full logic.',
  casesPerLayer: 'Based on order rules quantity if it exists, otherwise the large pallet build quantity.',
  pyPlus6AvgCases: 'Average cases sold over the prior years upcoming 6 weeks.',
  pyMinus6AvgCases: 'Average cases sold over the prior years trailing 6 weeks.',
  cyMinus6AvgCases: 'Average cases sold over the current year trailing 6 weeks.',
  pyPlus6GteLayer: 'Y when the PY +6 average meets or exceeds cases per layer, input into the recommendation logic.',
  pyMinus6GteLayer: ' Y when the PY -6 average meets or exceeds cases per layer, input to the recommendation logic.',
  cyMinus6GteLayer: 'Y when the CY -6 average meets or exceeds cases per layer, input to the recommendation logic.',
};

/** Total pixel width of the SKU Recommendations table */
const RECO_TABLE_MIN_WIDTH = recommendationColumns.reduce((acc, col) => acc + (col.minWidth ?? 120), 0);

/** Fixed row height — required for FlatList.getItemLayout */
const ROW_HEIGHT = 44;
/** Max rows shown before the body becomes vertically scrollable */
const MAX_VISIBLE_ROWS = 12;

const openColumns: FlexibleColumn[] = [
  { key: 'region', label: 'Region', flex: 1.05, align: 'left' },
  { key: 'market', label: 'Market', flex: 1.65, align: 'left' },
  { key: 'location', label: 'Location', flex: 1.25, align: 'left' },
  { key: 'customerBp', label: 'Customer BP', flex: 1.2, align: 'right' },
  { key: 'customer', label: 'Customer', flex: 1.7, align: 'left' },
  { key: 'high', label: 'High', flex: 0.75, align: 'center' },
  { key: 'medium', label: 'Medium', flex: 0.9, align: 'center' },
  { key: 'low', label: 'Low', flex: 0.75, align: 'center' },
  { key: 'total', label: 'Total', flex: 0.85, align: 'center' },
];

const RecommendationsTab: React.FC<RecommendationsTabProps> = ({
  loading,
  recommendationRows,
  openRecommendationRows,
  recommendationWindowStart,
  recommendationWindowEnd,
  sort: recoSort,
  onSort: handleRecoSort,
  pagination,
  onExport,
}) => {
  const [hoveredRecommendationRowIndex, setHoveredRecommendationRowIndex] = useState<number | null>(null);
  const [hoveredOpenRecommendationRowIndex, setHoveredOpenRecommendationRowIndex] = useState<number | null>(null);
  const [openSort, setOpenSort] = useState<SortState>({ col: null, dir: null });
  const [openPage, setOpenPage] = useState<number>(0);
  const [openPageSize, setOpenPageSize] = useState<number>(25);

  // ── Scroll sync refs for SKU Recommendations (header ↔ body, horizontal) ──
  const recoHeaderScrollRef = useRef<ScrollView>(null);
  const recoBodyScrollRef = useRef<ScrollView>(null);
  const isRecoHeaderScrolling = useRef(false);
  const isRecoBodyScrolling = useRef(false);

  const onRecoHeaderScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isRecoBodyScrolling.current) return;
    isRecoHeaderScrolling.current = true;
    recoBodyScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isRecoHeaderScrolling.current = false;
  }, []);

  const onRecoBodyScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isRecoHeaderScrolling.current) return;
    isRecoBodyScrolling.current = true;
    recoHeaderScrollRef.current?.scrollTo({ x: e.nativeEvent.contentOffset.x, animated: false });
    isRecoBodyScrolling.current = false;
  }, []);

  const handleOpenSort = makeHandleSort(setOpenSort);

  // SKU Recommendations rows are already sorted server-side (sortBy/sortOrder
  // in the API request), so no client-side re-sort is applied here.
  const sortedRecommendationRows = recommendationRows;

  const sortedOpenRows = useMemo(
    () => applySort(openRecommendationRows, openSort, OPEN_RECO_NUMERIC_KEYS),
    [openRecommendationRows, openSort],
  );

  const totalOpenElements = sortedOpenRows.length;
  const totalOpenPages = Math.ceil(totalOpenElements / openPageSize);

  const paginatedOpenRows = useMemo(() => {
    const start = openPage * openPageSize;
    return sortedOpenRows.slice(start, start + openPageSize);
  }, [sortedOpenRows, openPage, openPageSize]);

  const recoBodyHeight = useMemo(
    () => Math.min(sortedRecommendationRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT,
    [sortedRecommendationRows.length],
  );

  const openBodyHeight = useMemo(
    () => Math.min(paginatedOpenRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT,
    [paginatedOpenRows.length],
  );

  const getItemLayout = useCallback(
    (_: any, index: number) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index }),
    [],
  );

  const isRowHoverable = Platform.OS === 'web';
  const recoContentWidthStyle = useMemo(() => ({ width: RECO_TABLE_MIN_WIDTH }), []);

  // ── Render callback: SKU Recommendations row ──────────────────────────────
  const renderRecoRow = useCallback(
    ({ item: row, index: rowIndex }: { item: Record<string, any>; index: number }) => {
      const isHovered = hoveredRecommendationRowIndex === rowIndex;
      return (
        <Pressable
          style={[styles.tableDataRow, isHovered && styles.tableDataRowHover]}
          onHoverIn={isRowHoverable ? () => setHoveredRecommendationRowIndex(rowIndex) : undefined}
          onHoverOut={isRowHoverable ? () => setHoveredRecommendationRowIndex(null) : undefined}
        >
          {recommendationColumns.map((column) => {
            const value = row[column.key];
            return (
              <View
                key={column.key}
                style={[
                  styles.tableDataCell,
                  { width: column.minWidth ?? 120, minWidth: column.minWidth ?? 120, maxWidth: column.minWidth ?? 120, overflow: 'hidden' },
                  column.align === 'right' && styles.alignRight,
                  column.align === 'center' && styles.alignCenter,
                ]}
              >
                {column.key === 'confidence' ? (
                  renderConfidencePill(String(value ?? 'Low'))
                ) : column.key === 'location' ? (
                  <TouchableOpacity activeOpacity={0.75} onPress={() => {}}>
                    <Text style={styles.tableLinkText} numberOfLines={1}>{String(value ?? '-')}</Text>
                  </TouchableOpacity>
                ) : column.key === 'customerEnrolled' ? (
                  String(value ?? 'N') === 'Y' ? (
                    <View style={styles.tableNeutralTag}><Text style={styles.tableNeutralTagText}>Yes</Text></View>
                  ) : (
                    <Text style={styles.tableMutedText} numberOfLines={1}>No</Text>
                  )
                ) : column.key === 'recommendation' ? (
                  <Text style={[styles.tableDataText, styles.tableDataTextStrong]} numberOfLines={1}>
                    {String(value ?? '-') === 'Turn On' ? 'Y' : String(value ?? '-') === 'Turn Off' ? 'N' : String(value ?? '-')}
                  </Text>
                ) : (
                  <Text style={styles.tableDataText} numberOfLines={1}>{String(value ?? '-')}</Text>
                )}
              </View>
            );
          })}
        </Pressable>
      );
    },
    [hoveredRecommendationRowIndex, isRowHoverable],
  );

  // ── Render callback: Open Recommendations row ─────────────────────────────
  const renderOpenRow = useCallback(
    ({ item: row, index: rowIndex }: { item: Record<string, any>; index: number }) => {
      const isHovered = hoveredOpenRecommendationRowIndex === rowIndex;
      return (
        <Pressable
          style={[styles.tableDataRow, isHovered && styles.tableDataRowHover]}
          onHoverIn={isRowHoverable ? () => setHoveredOpenRecommendationRowIndex(rowIndex) : undefined}
          onHoverOut={isRowHoverable ? () => setHoveredOpenRecommendationRowIndex(null) : undefined}
        >
          {openColumns.map((column) => {
            const value = row[column.key];
            return (
              <View
                key={column.key}
                style={[
                  styles.openRecoCell,
                  { flex: column.flex },
                  column.align === 'right' && styles.alignRight,
                  column.align === 'center' && styles.alignCenter,
                ]}
              >
                {column.key === 'location' ? (
                  <TouchableOpacity activeOpacity={0.75} onPress={() => {}}>
                    <Text style={styles.tableLinkText}>{String(value ?? '-')}</Text>
                  </TouchableOpacity>
                ) : column.key === 'total' ? (
                  <Text style={[styles.tableDataText, styles.tableDataTextStrong]}>{String(value ?? '-')}</Text>
                ) : (
                  <Text style={styles.tableDataText}>{String(value ?? '-')}</Text>
                )}
              </View>
            );
          })}
        </Pressable>
      );
    },
    [hoveredOpenRecommendationRowIndex, isRowHoverable],
  );

  return (
    <View style={styles.sectionGap}>

      {/* ════ SKU RECOMMENDATIONS CARD ════════════════════════════════════════ */}
      <View style={styles.card}>
        <View style={styles.cardHeaderRow}>
          <View>
            <View style={styles.kpiLabelRow}>
              <Text style={styles.cardTitle}>SKU Recommendations</Text>
              <DLOTooltip
                content="Scope: recommendations appear only when adding or removing a rule. Walmart accounts are excluded unless the SKU is an allied brand. Only layer pickable packages are included, and any saleable item with sales in the past year plus six weeks."
                position="auto"
                trigger="hover"
                maxWidth={280}
              >
                <View style={styles.maxPotentialInfoBtn}>
                  <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                </View>
              </DLOTooltip>
            </View>
            <Text style={styles.cardSubTitle}>
              {'SKU x customer combinations evaluated against the 6 week layer logic'}
              {recommendationWindowStart && recommendationWindowEnd
                ? ` · Recommendation window: ${formatWindowDate(recommendationWindowStart, false)} to ${formatWindowDate(recommendationWindowEnd, true)}`
                : ''}
            </Text>
          </View>
          <ExportButton testID="order-rules-export-recommendations" onExport={onExport} />
        </View>

        {loading ? (
          <TableSkeleton rows={9} />
        ) : recommendationRows.length === 0 ? (
          <View style={styles.emptyStateWrap}>
            <Text style={styles.emptyStateText}>No recommendations for the current selection.</Text>
          </View>
        ) : (
          <View>
            {/* Sticky header — outside the fixed-height body */}
            <ScrollView
              ref={recoHeaderScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              onScroll={onRecoHeaderScroll}
              bounces={false}
              contentContainerStyle={recoContentWidthStyle}
            >
              <View style={styles.tableHeaderRow}>
                {recommendationColumns.map((column) => {
                  const isActive = recoSort.col === column.key;
                  return (
                    <TouchableOpacity
                      key={column.key}
                      style={[
                        styles.tableHeaderCell,
                        { width: column.minWidth ?? 120, minWidth: column.minWidth ?? 120, maxWidth: column.minWidth ?? 120 },
                        column.align === 'right' && styles.tableHeaderCellAlignRight,
                        column.align === 'center' && styles.tableHeaderCellAlignCenter,
                      ]}
                      onPress={() => handleRecoSort(column.key)}
                      activeOpacity={0.7}
                      accessibilityLabel={`Sort by ${column.label}`}
                    >
                      <View style={[styles.maxPotentialHeaderTextWrap, { width: undefined, flex: 1 }]}>
                        <Text style={styles.tableHeaderText}>{column.label}</Text>
                        {column.key === 'confidence' ? (
                          <DLOTooltip
                            content=""
                            customContent={<ConfidenceLogicTooltipContent />}
                            title="Recommendation and confidence logic"
                            trigger="hover"
                            position="auto"
                            contentHeightHint={300}
                            maxWidth={360}
                          >
                            <View style={styles.maxPotentialHeaderInfoIcon}>
                              <MaterialIcons name="info-outline" size={12} color="#818181" />
                            </View>
                          </DLOTooltip>
                        ) : recommendationHeaderTooltips[column.key] ? (
                          <DLOTooltip content={recommendationHeaderTooltips[column.key]} trigger="hover" position="auto" maxWidth={300}>
                            <View style={styles.maxPotentialHeaderInfoIcon}>
                              <MaterialIcons name="info-outline" size={12} color="#818181" />
                            </View>
                          </DLOTooltip>
                        ) : null}
                      </View>
                      <MaterialIcons
                        name={isActive ? (recoSort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                        size={14}
                        color={isActive ? '#005CBC' : '#9E9E9E'}
                        style={styles.tableHeaderSortIcon}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>

            {/* Fixed-height body — rows scroll inside, not the page */}
            <View style={{ height: recoBodyHeight, overflow: 'hidden' }}>
              <ScrollView
                ref={recoBodyScrollRef}
                horizontal
                showsHorizontalScrollIndicator
                scrollEventThrottle={16}
                onScroll={onRecoBodyScroll}
                bounces={false}
                contentContainerStyle={recoContentWidthStyle}
              >
                <FlatList
                  data={sortedRecommendationRows}
                  renderItem={renderRecoRow}
                  keyExtractor={(_, index) => `reco-row-${index}`}
                  getItemLayout={getItemLayout}
                  showsVerticalScrollIndicator
                  removeClippedSubviews
                  maxToRenderPerBatch={20}
                  windowSize={5}
                  initialNumToRender={15}
                />
              </ScrollView>
            </View>
            {pagination}
          </View>
        )}
      </View>

      {/* ════ OPEN RECOMMENDATIONS CARD ══════════════════════════════════════ */}
      <View style={styles.card}>
        <View style={styles.cardHeaderRow}>
          <View>
            <View style={styles.kpiLabelRow}>
              <Text style={styles.cardTitle}>Open Recommendations by Customer</Text>
              <DLOTooltip
                content="Count of recommendation candidates identified but not yet actioned. The remaining follow up workload for business and warehouse leaders."
                position="auto"
                trigger="hover"
                maxWidth={280}
              >
                <View style={styles.maxPotentialInfoBtn}>
                  <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                </View>
              </DLOTooltip>
            </View>
            <Text style={styles.cardSubTitle}>Identified but not yet actioned, grouped by confidence</Text>
          </View>
          <ExportButton testID="order-rules-export-open-recommendations" onExport={onExport} />
        </View>

        {loading ? (
          <TableSkeleton rows={9} />
        ) : openRecommendationRows.length === 0 ? (
          <View style={styles.emptyStateWrap}>
            <Text style={styles.emptyStateText}>No open recommendations for the current selection</Text>
          </View>
        ) : (
          <View style={[styles.tableWrap, styles.openRecoTableWrap]}>
            {/* Sticky header — outside the fixed-height body */}
            <View style={styles.tableHeaderRow}>
              {openColumns.map((column) => {
                const isActive = openSort.col === column.key;
                return (
                  <TouchableOpacity
                    key={column.key}
                    style={[
                      styles.openRecoCell,
                      styles.openRecoCellSortHeader,
                      { flex: column.flex },
                      column.align === 'right' && styles.tableHeaderCellAlignRight,
                      column.align === 'center' && styles.tableHeaderCellAlignCenter,
                    ]}
                    onPress={() => handleOpenSort(column.key)}
                    activeOpacity={0.7}
                    accessibilityLabel={`Sort by ${column.label}`}
                  >
                    <Text style={styles.tableHeaderText}>{column.label}</Text>
                    <MaterialIcons
                      name={isActive ? (openSort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                      size={14}
                      color={isActive ? '#005CBC' : '#9E9E9E'}
                      style={styles.tableHeaderSortIcon}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Fixed-height body — only rows scroll vertically inside this box */}
            <View style={{ height: openBodyHeight, overflow: 'hidden' }}>
              <FlatList
                data={paginatedOpenRows}
                renderItem={renderOpenRow}
                keyExtractor={(_, index) => `open-reco-row-${openPage * openPageSize + index}`}
                getItemLayout={getItemLayout}
                showsVerticalScrollIndicator
                removeClippedSubviews
                maxToRenderPerBatch={20}
                windowSize={5}
                initialNumToRender={15}
              />
            </View>

            {/* Client-side pagination bar for Open Recommendations */}
            {totalOpenElements > 0 && (
              <View style={styles.paginationRow}>
                <Text style={styles.paginationLabel}>
                  {`Showing ${openPage * openPageSize + 1}–${Math.min((openPage + 1) * openPageSize, totalOpenElements)} of ${totalOpenElements.toLocaleString('en-US')}`}
                </Text>
                <View style={styles.paginationBtnGroup}>
                  <TouchableOpacity
                    style={[styles.paginationBtn, openPage === 0 && styles.paginationBtnDisabled]}
                    disabled={openPage === 0}
                    onPress={() => setOpenPage((p) => Math.max(0, p - 1))}
                    accessibilityLabel="Previous page"
                  >
                    <MaterialIcons name="chevron-left" size={18} color={openPage === 0 ? '#9CA3AF' : '#FFFFFF'} />
                  </TouchableOpacity>
                  <Text style={styles.paginationPageText}>{`Page ${openPage + 1} of ${Math.max(totalOpenPages, 1)}`}</Text>
                  <TouchableOpacity
                    style={[styles.paginationBtn, openPage >= totalOpenPages - 1 && styles.paginationBtnDisabled]}
                    disabled={openPage >= totalOpenPages - 1}
                    onPress={() => setOpenPage((p) => Math.min(totalOpenPages - 1, p + 1))}
                    accessibilityLabel="Next page"
                  >
                    <MaterialIcons name="chevron-right" size={18} color={openPage >= totalOpenPages - 1 ? '#9CA3AF' : '#FFFFFF'} />
                  </TouchableOpacity>
                </View>
                <View style={styles.paginationSizeSelector}>
                  {[25, 50, 100].map((size) => (
                    <TouchableOpacity
                      key={size}
                      style={[styles.paginationSizeOption, openPageSize === size && styles.paginationSizeOptionActive]}
                      onPress={() => {
                        setOpenPageSize(size);
                        setOpenPage(0);
                      }}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.paginationSizeText, openPageSize === size && styles.paginationSizeTextActive]}>
                        {size}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

export default RecommendationsTab;

